import Layout from "@/components/feature/Layout";
import PageHero from "@/components/base/PageHero";
import SectionHeader from "@/components/base/SectionHeader";
import { useState } from "react";

const CONTACT_FORM_URL = "https://readdy.ai/api/form/dabf64sldn1crcvmigmg";

const contactReasons = [
  {
    title: "General Inquiry",
    description: "Questions about Mitaja Corporation, our services, or general information.",
    icon: "ri-mail-line",
  },
  {
    title: "Government Contracting",
    description: "Inquiries related to federal, state, or local government contracting and procurement.",
    icon: "ri-government-line",
  },
  {
    title: "Teaming & Partnerships",
    description: "Opportunities to partner or team on government and commercial technology programs.",
    icon: "ri-star-line",
  },
  {
    title: "Business Development",
    description: "Business development inquiries, proposal requests, and partnership discussions.",
    icon: "ri-briefcase-line",
  },
  {
    title: "Careers",
    description: "Job opportunities, resume submissions, and career-related questions.",
    icon: "ri-team-line",
  },
];

export default function Contact() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("website_alt") as string;
    if (honeypot && honeypot.trim().length > 0) {
      setFormStatus("success");
      form.reset();
      return;
    }

    setFormStatus("submitting");
    setFormError("");

    // Empty honeypot fields are not included in the request payload.
    formData.delete("website_alt");

    try {
      const res = await fetch(CONTACT_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        setFormError(serverMsg || "There was a problem submitting your inquiry. Please try again.");
        setFormStatus("idle");
      }
    } catch {
      setFormError("There was a problem submitting your inquiry. Please try again.");
      setFormStatus("idle");
    }
  };

  const inputClass =
    "w-full px-4 py-2.5 rounded-md border border-background-200/80 bg-white text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100";
  const labelClass = "block text-sm font-medium text-foreground-700 mb-1.5";

  return (
    <Layout>
      <PageHero
        title="Contact Mitaja"
        subtitle="Get in Touch"
        description="Contact Mitaja Corporation for inquiries about government contracting, teaming opportunities, business development, or careers."
        size="md"
      />

      <section className="section-padding bg-white">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <SectionHeader
                title="Contact Us"
                description="Reach out to Mitaja Corporation for your inquiry."
                centered={false}
              />
              <div className="space-y-6 mb-10">
                {contactReasons.map((reason) => (
                  <div key={reason.title} className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                      <i className={`${reason.icon} text-primary-600 text-xl`} aria-hidden="true"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-foreground-900 mb-1">
                        {reason.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {reason.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-background-100 rounded-lg p-6 border border-background-200/80">
                <h3 className="text-sm font-bold text-foreground-800 uppercase tracking-wider mb-3">
                  Note
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Private personal contact information is not published on this website. Please use the form to submit your inquiry and we will respond to you directly. For urgent matters, contact information can be requested through the form.
                </p>
              </div>
            </div>

            <div>
              <div className="card-base p-8">
                <h3 className="text-xl font-bold text-foreground-900 mb-2">
                  Send an Inquiry
                </h3>
                <p className="text-sm text-foreground-500 mb-2">
                  Fill out the form below and we will route your inquiry to the appropriate team.
                </p>
                <p className="text-xs text-foreground-400 mb-6">
                  Fields marked with an asterisk (<span aria-hidden="true">*</span>) are required.
                </p>

                {formStatus === "success" ? (
                  <div
                    role="status"
                    aria-live="polite"
                    className="bg-accent-50 border border-accent-200 rounded-lg p-6 text-center"
                  >
                    <i className="ri-check-line text-accent-600 text-2xl mb-2" aria-hidden="true"></i>
                    <p className="text-sm font-semibold text-accent-700 mb-1">
                      Thank you for your inquiry.
                    </p>
                    <p className="text-sm text-accent-600">
                      We have received your message and will respond to you shortly.
                    </p>
                    <button
                      onClick={() => setFormStatus("idle")}
                      className="mt-4 text-sm font-semibold text-accent-600 hover:text-accent-700 cursor-pointer"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                    data-readdy-form="contact-inquiry"
                    noValidate
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-firstName" className={labelClass}>
                          First Name <span aria-hidden="true" className="text-red-600">*</span>
                        </label>
                        <input
                          id="contact-firstName"
                          type="text"
                          name="firstName"
                          required
                          aria-required="true"
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-lastName" className={labelClass}>
                          Last Name <span aria-hidden="true" className="text-red-600">*</span>
                        </label>
                        <input
                          id="contact-lastName"
                          type="text"
                          name="lastName"
                          required
                          aria-required="true"
                          className={inputClass}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-email" className={labelClass}>
                        Email <span aria-hidden="true" className="text-red-600">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        aria-required="true"
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-company" className={labelClass}>
                        Company / Organization
                      </label>
                      <input
                        id="contact-company"
                        type="text"
                        name="company"
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-inquiryType" className={labelClass}>
                        Inquiry Type <span aria-hidden="true" className="text-red-600">*</span>
                      </label>
                      <select
                        id="contact-inquiryType"
                        name="inquiryType"
                        required
                        aria-required="true"
                        className={inputClass}
                      >
                        <option value="">Select inquiry type</option>
                        <option value="general">General Inquiry</option>
                        <option value="government">Government Contracting</option>
                        <option value="teaming">Teaming &amp; Partnerships</option>
                        <option value="business">Business Development</option>
                        <option value="careers">Careers</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="contact-message" className={labelClass}>
                        Message <span aria-hidden="true" className="text-red-600">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        aria-required="true"
                        className={`${inputClass} resize-none`}
                        placeholder="Describe your inquiry..."
                      />
                      <p className="text-xs text-foreground-400 mt-1">
                        Maximum 500 characters
                      </p>
                    </div>

                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />

                    {formError && (
                      <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm text-red-700">{formError}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === "submitting"}
                      className="btn-primary w-full sm:w-auto disabled:opacity-50"
                    >
                      {formStatus === "submitting" ? "Sending..." : "Send Inquiry"}
                      <i className="ri-arrow-right-line ml-2" aria-hidden="true"></i>
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}